import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Spinner } from '../shared/Spinner';

interface OtpModalProps {
  email: string;
  onSuccess: () => void;
  onClose: () => void;
}

export const OtpModal: React.FC<OtpModalProps> = ({ email, onSuccess, onClose }) => {
  const { login, requestOtp } = useAuth();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [pasteSuccess, setPasteSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleSubmit = useCallback(async (otpCode?: string) => {
    const codeToSubmit = otpCode || code.join('');
    
    if (codeToSubmit.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await login(email, codeToSubmit);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
      // Clear the code on error
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }, [code, email, login, onSuccess]);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Auto-paste functionality when clicking into input boxes
  useEffect(() => {
    const checkClipboardAndPaste = async () => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          const clipboardText = await navigator.clipboard.readText();
          const cleanText = clipboardText.replace(/\D/g, '');
          
          // If clipboard contains exactly 6 digits and current code is empty
          if (cleanText.length === 6 && code.every(digit => !digit)) {
            const newCode = cleanText.split('');
            setCode(newCode);
            setError('');
            
            // Show paste success feedback
            setPasteSuccess(true);
            setTimeout(() => setPasteSuccess(false), 2000);
            
            // Focus the last input
            inputRefs.current[5]?.focus();
            
            // Auto-submit
            handleSubmit(cleanText);
          }
        }
      } catch (err) {
        // Clipboard access failed or not available - this is normal
        console.debug('Clipboard access not available or failed:', err);
      }
    };

    // Add click listeners to input boxes for auto-paste
    const inputs = inputRefs.current;
    const handleInputClick = () => {
      // Only try auto-paste if all inputs are empty
      if (code.every(digit => !digit)) {
        checkClipboardAndPaste();
      }
    };

    inputs.forEach(input => {
      if (input) {
        input.addEventListener('click', handleInputClick);
      }
    });

    // Cleanup
    return () => {
      inputs.forEach(input => {
        if (input) {
          input.removeEventListener('click', handleInputClick);
        }
      });
    };
  }, [code, handleSubmit]); // Re-run when code changes

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digits
    if (value && !/^\d$/.test(value)) return; // Only allow digits

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (value && index === 5 && newCode.every(digit => digit)) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length >= 1) {
      // Fill as many boxes as we have digits
      const newCode = [...code];
      for (let i = 0; i < Math.min(pastedData.length, 6); i++) {
        newCode[i] = pastedData[i];
      }
      setCode(newCode);
      setError('');
      
      // Show paste success feedback
      setPasteSuccess(true);
      setTimeout(() => setPasteSuccess(false), 2000);
      
      // Focus the next empty input or the last input if all are filled
      const nextEmptyIndex = newCode.findIndex(digit => !digit);
      const focusIndex = nextEmptyIndex === -1 ? 5 : Math.min(nextEmptyIndex, 5);
      inputRefs.current[focusIndex]?.focus();
      
      // Auto-submit if we have exactly 6 digits
      if (pastedData.length === 6) {
        handleSubmit(pastedData);
      }
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');

    try {
      await requestOtp(email);
      setResendCooldown(60); // 60 second cooldown
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white/20 backdrop-blur-md rounded-2xl p-8 max-w-md w-full mx-auto border border-white/30">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2 text-white">Enter Verification Code</h2>
            <p className="text-white/90 text-sm">
              We've sent a 6-digit code to
              <br />
              <span className="font-medium text-white">{email}</span>
            </p>
            {pasteSuccess && (
              <div className="mt-2 text-green-200 text-sm bg-green-500/20 border border-green-300/30 rounded-lg p-2">
                ✓ Code pasted successfully!
              </div>
            )}
          </div>

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className="flex justify-center space-x-3 mb-4">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-12 h-12 text-center text-xl font-bold border-2 border-white/30 rounded-lg focus:border-white/70 focus:outline-none bg-white/20 text-white placeholder-white/60"
                disabled={loading}
              />
            ))}
          </div>
          
          <p className="text-white/70 text-xs text-center mb-6">
            💡 Tip: Triple-click the code in your email to select it, copy (Ctrl+C), then click any input box to auto-paste
            <br />
            Or paste manually with Ctrl+V and it will auto-fill all boxes
          </p>

          {error && (
            <div className="text-red-200 text-sm bg-red-500/20 border border-red-300/30 rounded-lg p-3 mb-4 text-center">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || code.some(digit => !digit)}
            className="w-full bg-white text-primary-600 font-semibold py-3 px-4 rounded-lg hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 mb-4 flex items-center justify-center"
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </button>
        </form>

        <div className="text-center space-y-3">
          <button
            onClick={handleResend}
            disabled={loading || resendCooldown > 0}
            className="text-white/90 hover:text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendCooldown > 0 
              ? `Resend code in ${resendCooldown}s`
              : 'Resend code'
            }
          </button>

          <div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
